import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { Calendar } from 'react-date-range'
import { format, parse, isValid } from 'date-fns'
import ruLocale from 'date-fns/locale/ru'
import kzLocale from 'date-fns/locale/kk'
import { getCurrentLanguage } from '@/i18next'
import { Calendar as CalendarIcon } from '@app/images'
import { useToggle, useClickOutside } from '@/shared'
import { Icon, Button } from '@/ui'
import styles from './DatePicker.module.scss'
import { useTranslation } from 'react-i18next'

export const DatePicker = ({
	value = '',
	dateFormat = 'dd.MM.yyyy',
	onChange = function () {},
	onCustomChange,
	placeholder,
	showIcon = true,
	inputClassName = '',
	wrapperClasses = '',
	iconClasses = '',
	calendarClassName = '',
	minDate,
	maxDate,
	error,
	disabled = false,
	disabledDates,
	enabledDates,
	alwaysOpen = false,
	onClose,
	...props
}) => {
	const [isOpen, toggleOpen] = useToggle(alwaysOpen)
	const [textValue, setTextValue] = useState(value || '')
	const datePickerRef = useRef(null)
	const lang = getCurrentLanguage()
	const { t } = useTranslation()

	const inputRef = useRef(null)

	const onChangeValue = useCallback(
		(value) => {
			const date = format(value, dateFormat)
			setTextValue(date)
			if (onCustomChange) onCustomChange(value)
			onChange(date)
			if (alwaysOpen && onClose) {
				onClose()
			} else {
				toggleOpen(false)
			}
		},
		[dateFormat, onChange, toggleOpen, alwaysOpen, onClose]
	)

	const onToggleOpen = useCallback(
		(e) => {
			e.stopPropagation()
			!disabled && toggleOpen()
		},
		[toggleOpen, disabled]
	)

	const onClickOutside = useCallback(
		(event) => {
			if (alwaysOpen && onClose) {
				onClose()
			} else {
				event.target.blur()
				toggleOpen()
			}
		},
		[toggleOpen, alwaysOpen, onClose]
	)

	const handleInputClick = useCallback(() => !isOpen && toggleOpen(), [isOpen, toggleOpen])

	const onTypeDate = useCallback(
		(event) => {
			let { value } = event.target

			// Очищаем значение от недопустимых символов, оставляем только цифры и точки
			value = value.replace(/[^0-9.]/g, '')

			// Добавляем точку после дня и месяца, если они введены
			if (value.length === 2 || value.length === 5) {
				value += '.'
			}

			setTextValue(value)

			// Проверяем, если поле ввода пустое, то вызываем onChange с пустой строкой
			if (value === '') {
				onChange('')
				return
			}

			// Проверяем, если год больше 2100, обрезаем его до 2100
			if (value.length >= 10) {
				let year = parseInt(value.slice(6, 10), 10)

				// Проверяем, если год меньше 1900, устанавливаем его на 1900
				if (year < 1900) {
					value = value.slice(0, 6) + '1900' + value.slice(10)
					setTextValue(value)
				} else if (year > 2100) {
					value = value.slice(0, 6) + '2100' + value.slice(10)
					setTextValue(value)
				}

				const date = parse(value, dateFormat, new Date())
				if (isValid(date)) {
					onChange(format(date, dateFormat))
				}
			}
		},
		[dateFormat, onChange]
	)

	const onBackspace = useCallback(
		(event) => {
			if (event.key === 'Backspace') {
				let { value } = event.target

				// Удаляем последний символ
				value = value.slice(0, -1)

				// Если удаляем точку, то удаляем предыдущий символ тоже
				if (value.endsWith('.')) {
					value = value.slice(0, -1)
				}

				setTextValue(value)

				// Проверяем, если поле ввода пустое, то вызываем onChange с пустой строкой
				if (value === '') {
					onChange('')
					event.preventDefault()
				}
			}
		},
		[onChange]
	)

	const formattedDate = useMemo(() => (value ? parse(value, dateFormat, new Date()) : null), [dateFormat, value])

	useClickOutside(datePickerRef, isOpen || alwaysOpen, onClickOutside)

	useEffect(() => (!value ? setTextValue('') : setTextValue(value)), [value])

	const width = useMemo(() => datePickerRef?.current?.offsetWidth, [datePickerRef?.current?.offsetWidth])

	// Функция для проверки, является ли дата разрешенной
	const isDateEnabled = useCallback(
		(date) => {
			if (!enabledDates || enabledDates.length === 0) {
				return true // Если enabledDates не задан, все даты разрешены
			}

			return enabledDates.some((enabledDate) => {
				if (enabledDate instanceof Date && typeof enabledDate.getTime === 'function') {
					const enabledDateNormalized = new Date(
						enabledDate.getFullYear(),
						enabledDate.getMonth(),
						enabledDate.getDate()
					)
					const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate())
					return enabledDateNormalized.getTime() === dateNormalized.getTime()
				}
				return false
			})
		},
		[enabledDates]
	)

	// Функция для определения заблокированных дат
	const isDateDisabled = useCallback(
		(date) => {
			// Сначала проверяем стандартные заблокированные даты
			if (disabledDates && disabledDates.length > 0) {
				const isInDisabledDates = disabledDates.some((disabledDate) => {
					if (disabledDate instanceof Date && typeof disabledDate.getTime === 'function') {
						const disabledDateNormalized = new Date(
							disabledDate.getFullYear(),
							disabledDate.getMonth(),
							disabledDate.getDate()
						)
						const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate())
						return disabledDateNormalized.getTime() === dateNormalized.getTime()
					}
					return false
				})
				if (isInDisabledDates) return true
			}

			// Если есть enabledDates, проверяем инверсию
			if (enabledDates && enabledDates.length > 0) {
				return !isDateEnabled(date)
			}

			return false
		},
		[disabledDates, enabledDates, isDateEnabled]
	)

	// Определяем какие даты использовать для календаря
	const calendarDisabledDates = useMemo(() => {
		if (enabledDates && enabledDates.length > 0) {
			// Если есть enabledDates, не передаем disabledDates, используем dayContentRenderer
			return []
		}
		return disabledDates || []
	}, [enabledDates, disabledDates])

	if (alwaysOpen) {
		return (
			<div ref={datePickerRef} className={styles.alwaysOpenContainer}>
				<Calendar
					locale={lang === 'rus' ? ruLocale : kzLocale}
					editableDateInputs={true}
					className={calendarClassName || `${styles.calendarAlwaysOpen}`}
					onChange={onChangeValue}
					date={formattedDate}
					minDate={minDate}
					maxDate={maxDate}
					disabled={disabled}
					style={{ width: width && width > 300 ? width : 300 }}
					disabledDates={calendarDisabledDates}
					dayContentRenderer={
						enabledDates && enabledDates.length > 0
							? (date) => {
									const isDisabled = isDateDisabled(date)
									return (
										<span
											style={{
												color: isDisabled ? '#ccc' : 'inherit',
												pointerEvents: isDisabled ? 'none' : 'auto',
											}}
										>
											{date.getDate()}
										</span>
									)
							  }
							: undefined
					}
				/>
			</div>
		)
	}

	return (
		<div className={`${styles.wrapper} ${wrapperClasses} ${disabled ? styles.disabled : ''}`} ref={datePickerRef}>
			<input
				className={`${styles.input} ${inputClassName}`}
				type="text"
				value={textValue}
				onChange={onTypeDate}
				onKeyDown={onBackspace}
				onClick={handleInputClick}
				placeholder={placeholder || t('default_date_value')}
				disabled={disabled}
				ref={inputRef}
				autoComplete="off"
				{...props}
			/>
			{showIcon && (
				<div className={styles.button}>
					<Button type="bare" onClick={onToggleOpen}>
						<Icon src={CalendarIcon} classes={iconClasses} />
					</Button>
				</div>
			)}
			{isOpen && (
				<Calendar
					locale={lang === 'rus' ? ruLocale : kzLocale}
					editableDateInputs={true}
					className={`${styles.calendar}`}
					onChange={onChangeValue}
					date={formattedDate}
					minDate={minDate}
					maxDate={maxDate}
					disabled={disabled}
					style={{ width: width && width > 300 ? width : 300 }}
					disabledDates={calendarDisabledDates}
					dayContentRenderer={
						enabledDates && enabledDates.length > 0
							? (date) => {
									const isDisabled = isDateDisabled(date)
									return (
										<span
											style={{
												color: isDisabled ? '#ccc' : 'inherit',
												pointerEvents: isDisabled ? 'none' : 'auto',
											}}
										>
											{date.getDate()}
										</span>
									)
							  }
							: undefined
					}
				/>
			)}
		</div>
	)
}
